import {SC_CrewBase} from "./crews/SC_CrewBase";
import {SC_ServiceInstancePhase} from "./enums/SC_ServiceInstancePhase";
import {SC_ForceType} from "./enums/SC_ForceType";
import {SC_GeoLocation} from "./SC_GeoLocation";
import {SC_IVRCompliance} from "./enums/SC_IVRCompliance";
import {SC_ServiceFile} from "./files/SC_ServiceFile";
import {SC_ServiceFileType} from "./enums/SC_ServiceFileType";
import {SC_ImageStatusesClone} from "./enums/SC_ImageStatusesClone";
import {SC_ServiceStatus} from "./enums/SC_ServiceStatus";

export class SC_ServiceInstance {
    static inputKeys = {
        internalID: 'internalID',
        parentID: 'parentID',
        crewID: 'crewID',
        serviceDate: 'servD',
        anticipatedDuration: 'anticipatedDuration',
        actualStart: 'actualStart',
        beforePicsTakenTime: 'beforePicsTakenTime',
        actualEnd: 'actualEnd',
        phase: 'phase',
        requireQC: 'requireQC',
        forceType: 'forceType',
        inCoords: 'inc',
        outCoords: 'ouc',
        techs: 'techs',
        anticipated_techs: 'atechs',
        rescheduled_sibbling: 'sibbl',
    } as const;

    internalID: number | null;
    parentID: number | null;
    crewID: number | null;
    crew: SC_CrewBase | null;
    serviceDate: Date | null;
    endDate: Date | null;
    anticipatedDuration: number | null;
    actualStart: Date | null;
    beforePicsTakenTime: Date | null;
    actualEnd: Date | null;
    phase: SC_ServiceInstancePhase;
    requireQC: boolean | null;
    forceType: SC_ForceType;

    inCoords: SC_GeoLocation | null;
    outCoords: SC_GeoLocation | null;

    actual_techs: number | null;
    anticipated_techs: number | null;

    rescheduledSibblingID: number | null;
    rescheduledSibbling: SC_ServiceInstance | null;

    constructor({
        internalID = null,
        parentID = null,
        crewID = null,
        anticipatedDuration = null,
        serviceDate = null,
        actualStart = null,
        beforePicsTakenTime = null,
        actualEnd = null,
        phase = new SC_ServiceInstancePhase(SC_ServiceInstancePhase.NULL),
        requireQC = null,
        forceType = new SC_ForceType(SC_ForceType.NULL),
        inCoords = null,
        outCoords = null,
        actual_techs = null,
                    anticipated_techs = null,
                    rescheduledSibblingID = null,
    } = {}) {
        this.internalID = internalID;
        this.parentID = parentID;
        this.crewID = crewID;
        this.serviceDate = null;
        this.endDate = null;

        if (serviceDate) {
            this.serviceDate = new Date(serviceDate);

            if (anticipatedDuration !== null) {
                let endd = new Date(serviceDate);
                endd.setSeconds(endd.getSeconds() + anticipatedDuration);
                this.endDate = endd;
            }
        }

        this.anticipatedDuration = anticipatedDuration;
        this.actualStart = actualStart ? new Date(actualStart) : null;
        this.beforePicsTakenTime = beforePicsTakenTime ? new Date(beforePicsTakenTime) : null;
        this.actualEnd = actualEnd ? new Date(actualEnd) : null;
        this.phase = phase;
        this.requireQC = requireQC;
        this.forceType = forceType;
        this.inCoords = inCoords;
        this.outCoords = outCoords;
        this.actual_techs = actual_techs;
        this.anticipated_techs = anticipated_techs;
        this.rescheduledSibblingID = rescheduledSibblingID;
    }

    static fromAPI(obj: Record<string, any>, crewsCache: SC_CrewBase[] = []): SC_ServiceInstance {
        const out = new SC_ServiceInstance({
            internalID: obj[SC_ServiceInstance.inputKeys.internalID],
            parentID: obj[SC_ServiceInstance.inputKeys.parentID],
            crewID: obj[SC_ServiceInstance.inputKeys.crewID],
            serviceDate: obj[SC_ServiceInstance.inputKeys.serviceDate],
            anticipatedDuration: obj[SC_ServiceInstance.inputKeys.anticipatedDuration],
            actualStart: obj[SC_ServiceInstance.inputKeys.actualStart],
            beforePicsTakenTime: obj[SC_ServiceInstance.inputKeys.beforePicsTakenTime],
            actualEnd: obj[SC_ServiceInstance.inputKeys.actualEnd],
            phase: SC_ServiceInstancePhase.fromAPI(obj[SC_ServiceInstance.inputKeys.phase] ?? SC_ServiceInstancePhase.NULL),
            requireQC: obj[SC_ServiceInstance.inputKeys.requireQC],
            forceType: SC_ForceType.fromAPI(obj[SC_ServiceInstance.inputKeys.forceType] ?? SC_ForceType.NULL),
            inCoords: SC_GeoLocation.fromAPI(obj[SC_ServiceInstance.inputKeys.inCoords] ?? null),
            outCoords: SC_GeoLocation.fromAPI(obj[SC_ServiceInstance.inputKeys.outCoords] ?? null),
            actual_techs: obj[SC_ServiceInstance.inputKeys.techs],
            anticipated_techs: obj[SC_ServiceInstance.inputKeys.anticipated_techs],
            rescheduledSibblingID: obj[SC_ServiceInstance.inputKeys.rescheduled_sibbling],
        });

        if (crewsCache)
            out.scavengeCrew(crewsCache);

        return out;
    }

    scavengeCrew(crewsCache: SC_CrewBase[]) {
        if (this.crewID && crewsCache)
            this.crew = crewsCache.find(c => c.internalID === this.crewID);
    }

    static sort(a: SC_ServiceInstance, b: SC_ServiceInstance): number {
        if (a.inProgress) {
            return -1;
        } else if (b.inProgress) {
            return 1;
        } else if (a.pending && b.completed) {
            return -1;
        } else if (b.pending && a.completed) {
            return 1;
        } else {
            return a.serviceDate!.getTime() - b.serviceDate!.getTime();
        }
    }

    get pending() {
        return this.phase.is(SC_ServiceInstancePhase.NOT_STARTED);
    }

    get started() {
        return this.phase.is(SC_ServiceInstancePhase.COMPLETE) || this.phase.is(SC_ServiceInstancePhase.BEFORE_PICS_TAKEN) || this.phase.is(SC_ServiceInstancePhase.CREW_PICS_TAKEN);
    }

    get inProgress() {
        return this.phase.is(SC_ServiceInstancePhase.BEFORE_PICS_TAKEN) || this.phase.is(SC_ServiceInstancePhase.CREW_PICS_TAKEN);
    }

    get completed() {
        return this.phase.is(SC_ServiceInstancePhase.COMPLETE);
    }

    get forceable() {
        return !this.phase.is(SC_ServiceInstancePhase.COMPLETE) || (this.forceType.is(SC_ForceType.RESCHEDULED) && !this.rescheduledSibblingID);
    }

    get duration(): number {
        return (this.actualEnd.getTime() - this.actualStart.getTime()) / 1000;
    }

    get etaMissed(): boolean {
        return !this.actualStart && (this.serviceDate < new Date()) || (this.actualStart && !this.actualEnd && (this.actualStart.getTime() + this.anticipatedDuration * 1000 < Date.now()));
    }

    get status(): SC_IVRCompliance {
        if (this.completed) {
            if (this.forceType.is(SC_ForceType.TRUANCY)) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.NO_WORK);
            } else if (this.forceType.is(SC_ForceType.RESCHEDULED)) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.RESCHEDULED);
            } else if (this.forceType.toAPI()) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.NO_IVR);
            } else {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.COMPLETED);
            }
        } else {
            if (!(this.crewID > 0)) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.PENDING_CREW_ASSIGNMENT);
            } else if (this.etaMissed) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.ETA_MISSED);
            } else if (this.inProgress) {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.ON_SITE);
            } else {
                return SC_IVRCompliance.fromAPI(SC_IVRCompliance.FUTURE)
            }
        }
    }

    static readonly MIN_IMAGES = 3;

    picturesStatus(files: SC_ServiceFile[]): SC_ImageStatusesClone {
        files = files.filter(it => it.type.isPicture && it.service_id === this.internalID);

        if (!files.length)
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.MISSING);

        let crews = files.filter(it => it.type.is(SC_ServiceFileType.CREW_PICTURE));
        let befores = files.filter(it => it.type.is(SC_ServiceFileType.QC_BEFORE));
        let afters = files.filter(it => it.type.is(SC_ServiceFileType.QC_AFTER));

        if (!befores.length && !afters.length)
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.MISSING);

        if (befores.length < SC_ServiceInstance.MIN_IMAGES || afters.length < SC_ServiceInstance.MIN_IMAGES)
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.MISSING);

        if (files.some(file => !file.uploadinged && !file.reviewed && !file.reported))
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.FINAL_REVIEW);

        if (!files.every(file => !file.uploadable() || file.uploadinged || file.reported))
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.FINAL_REVIEW);

        if (!crews.length || crews.some(file => file.reported))
            return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.CREW_PIC_ERROR);

        return SC_ImageStatusesClone.fromAPI(SC_ImageStatusesClone.COMPLETE);
    }

    get needsReschedule(): boolean {
        return this.forceType.is(SC_ForceType.RESCHEDULED) && !this.rescheduledSibblingID;
    }

    toRed() {
        // <div><span style={{color: "red"}}>Force Closed</span></div>
        switch (this.forceType.toAPI()) {
            case SC_ForceType.INSITE_FAILURE:
                return "Force Closed";
            case SC_ForceType.TOTAL_FAILURE:
                return "Force Closed: No IVR";
            case SC_ForceType.OUT_FAILURE:
                return "Force Closed: No Check Out";
            case SC_ForceType.OUT_INSITE_FAILURE:
                return "Force Closed: No Check Out";
            case SC_ForceType.TRUANCY:
                return "Force Closed: No Show";
            case SC_ForceType.RESCHEDULED:
                if (this.rescheduledSibblingID)
                    return "Force Closed: Rescheduled";
                return "Force Closed: Needs Rescheduling";
            case SC_ForceType.TRIP_CHARGE:
                return "Force Closed: Trip Charge";
            case SC_ForceType.NULL:
                return null;
            default:
                return "Not Set";
        }
    }
}